// MARK://dhk8/admin-shell/lib/clients/platform-orgs
// Organizations (multi-branch parent entity) → dhk8-platform-tenants.
//
// V1 endpoint exposed: GET /v1/orgs/:orgId/rollup/sales?from=ISO&to=ISO
// (commit 88e98c9 — read-only sales aggregation parent → child tenants).
// V2 (Stream 6 — reportes franquicia): /inventory /customers /products-top /kpis.
// Todos read-only; NO money (no comisiones / regalías / splits — decisión codificada).
//
// El endpoint de CRUD (POST /v1/organizations, GET /v1/organizations, etc.)
// AÚN no existe en el backend; cuando se agregue, este cliente lo absorbe.
// Mientras tanto, las orgs conocidas se cachean en localStorage (recent IDs)
// y el detalle lee branches del propio rollup (incluye displayName + slug).
//
// Para SUPER_ADMIN / merchants con varias sucursales — los SCMDG admins típicos
// (savis-coffee con N sucursales) son el caller principal.
import { PREVIEW_STUB, mock } from '../env.js'
import { gqlClient } from '../gql.js'
import {
  OrgSalesRollupQuery,
  OrgInventoryRollupQuery,
  OrgCustomersRollupQuery,
  OrgTopProductsRollupQuery,
  OrgKpisRollupQuery,
} from '../queries/platform-orgs.queries.js'

// F3.6 / ADR-0009: los rollups de organización (franquicia) se leen por el grafo
// (platform-tenants), con gate authorizeOrgAccess (owner del JWT). El gqlClient
// manda el JWT; el orgId es la clave del recurso (la org), no el tenant.
const NO_ORG = 'Organización no encontrada o sin acceso'


export interface OrgRollupBranch {
  /** liveTenantId UUID de la branch-account. */
  tenantId: string
  /** Slug público del tenant (= URL slug). Es lo que usa `Account.name` como label principal en la UI. */
  brand: string
  /** Razón social / displayName del tenant (== Tenant.legalName). Label secundario en la UI. */
  displayName: string
  /** Suma de PosOrder.totalCents cerradas en la ventana. */
  salesCents: number
  /** Cantidad de PosOrder cerradas (no canceladas) en la ventana. */
  orderCount: number
}

export interface OrgRollupTotals {
  salesCents: number
  orderCount: number
  branchCount: number
}

export interface OrgRollup {
  orgId: string
  orgSlug: string
  orgName: string
  period: { from: string | null; to: string | null }
  branches: OrgRollupBranch[]
  totals: OrgRollupTotals
}

export interface OrgSummary {
  id: string
  displayName: string
  /** Optional slug (when known). The rollup endpoint returns it; not all callers have it. */
  slug?: string
  /** When known, number of branches. */
  branchCount?: number
}

// ── recent orgs cache (localStorage) ───────────────────────────────────────
const STORAGE_KEY = 'dhk8-admin-recent-orgs'

function loadRecent(): OrgSummary[] {
  if (typeof localStorage === 'undefined') return []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as OrgSummary[]) : []
  } catch { return [] }
}
function saveRecent(orgs: OrgSummary[]): void {
  if (typeof localStorage === 'undefined') return
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(orgs.slice(0, 20))) } catch { /* ignore */ }
}
export function rememberOrg(summary: OrgSummary): void {
  const existing = loadRecent().filter((o) => o.id !== summary.id)
  saveRecent([summary, ...existing])
}
export function forgetOrg(id: string): void {
  saveRecent(loadRecent().filter((o) => o.id !== id))
}

// ── PREVIEW_STUB seed ──────────────────────────────────────────────────────
const STUB_ROLLUP: OrgRollup = {
  orgId: '00000000-0000-7000-8000-000000000001',
  orgSlug: 'savis-coffee',
  orgName: 'SAVIS COFFEE',
  period: { from: null, to: null },
  branches: [
    { tenantId: '00000000-0000-7000-8000-000000000010', brand: 'savis-coffee-gdl', displayName: 'SAVIS COFFEE GDL S.A. de C.V.', salesCents: 1_240_500, orderCount: 312 },
    { tenantId: '00000000-0000-7000-8000-000000000011', brand: 'savis-coffee-mty', displayName: 'SAVIS COFFEE MTY S.A. de C.V.', salesCents: 890_320, orderCount: 198 },
  ],
  totals: { salesCents: 2_130_820, orderCount: 510, branchCount: 2 },
}

// ── list orgs (best-effort) ────────────────────────────────────────────────
/**
 * V1: no hay un GET /v1/organizations todavía. Devolvemos la lista cacheada
 * en localStorage (orgs que el usuario ha visitado). El PREVIEW_STUB devuelve
 * la org "SAVIS COFFEE" de muestra para que el flujo se pueda probar sin backend.
 */
export async function listOrganizations(_signal?: AbortSignal): Promise<OrgSummary[]> {
  if (PREVIEW_STUB) {
    return mock([
      { id: STUB_ROLLUP.orgId, displayName: STUB_ROLLUP.orgName, slug: STUB_ROLLUP.orgSlug, branchCount: STUB_ROLLUP.branches.length },
    ])
  }
  return loadRecent()
}

// ── rollup ─────────────────────────────────────────────────────────────────
export async function getOrgRollup(
  orgId: string,
  period: { from?: string; to?: string } = {},
  signal?: AbortSignal,
): Promise<OrgRollup> {
  if (PREVIEW_STUB) return mock({ ...STUB_ROLLUP, orgId, period: { from: period.from ?? null, to: period.to ?? null } })
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(OrgSalesRollupQuery, { orgId, ...(period.from ? { from: period.from } : {}), ...(period.to ? { to: period.to } : {}) }, ctx).toPromise()
  if (res.error) throw res.error
  const d = res.data?.orgSalesRollup
  if (!d) throw new Error(NO_ORG)
  return d as OrgRollup
}

// ── Stream 6: inventory rollup ─────────────────────────────────────────────
export interface OrgInventoryRollupBranch {
  tenantId: string
  brand: string
  displayName: string
  stockItemCount: number
  totalQty: number
  lowStockCount: number
  outOfStockCount: number
}

export interface OrgInventoryLowStockItem {
  tenantId: string
  brand: string
  stockItemId: string
  sku: string | null
  productId: string
  qty: number
  reorderPoint: number
  minQty: number
  uom: string
}

export interface OrgInventoryRollup {
  orgId: string
  orgSlug: string
  orgName: string
  branches: OrgInventoryRollupBranch[]
  totals: {
    stockItemCount: number
    totalQty: number
    lowStockCount: number
    outOfStockCount: number
    branchCount: number
  }
  topLowStock: OrgInventoryLowStockItem[]
}

const STUB_INVENTORY: OrgInventoryRollup = {
  orgId: STUB_ROLLUP.orgId,
  orgSlug: STUB_ROLLUP.orgSlug,
  orgName: STUB_ROLLUP.orgName,
  branches: [
    { tenantId: STUB_ROLLUP.branches[0]!.tenantId, brand: STUB_ROLLUP.branches[0]!.brand, displayName: STUB_ROLLUP.branches[0]!.displayName, stockItemCount: 142, totalQty: 5_120.5, lowStockCount: 8, outOfStockCount: 2 },
    { tenantId: STUB_ROLLUP.branches[1]!.tenantId, brand: STUB_ROLLUP.branches[1]!.brand, displayName: STUB_ROLLUP.branches[1]!.displayName, stockItemCount: 118, totalQty: 3_980.0, lowStockCount: 12, outOfStockCount: 5 },
  ],
  totals: { stockItemCount: 260, totalQty: 9_100.5, lowStockCount: 20, outOfStockCount: 7, branchCount: 2 },
  topLowStock: [
    { tenantId: STUB_ROLLUP.branches[1]!.tenantId, brand: 'savis-coffee-mty', stockItemId: 'demo-1', sku: 'CAF-001', productId: 'p-1', qty: 0, reorderPoint: 20, minQty: 10, uom: 'KG' },
    { tenantId: STUB_ROLLUP.branches[0]!.tenantId, brand: 'savis-coffee-gdl', stockItemId: 'demo-2', sku: 'LEC-002', productId: 'p-2', qty: 3, reorderPoint: 15, minQty: 5, uom: 'L' },
  ],
}

export async function getOrgInventoryRollup(
  orgId: string,
  signal?: AbortSignal,
): Promise<OrgInventoryRollup> {
  if (PREVIEW_STUB) return mock({ ...STUB_INVENTORY, orgId })
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(OrgInventoryRollupQuery, { orgId }, ctx).toPromise()
  if (res.error) throw res.error
  const d = res.data?.orgInventoryRollup
  if (!d) throw new Error(NO_ORG)
  return d as OrgInventoryRollup
}

// ── Stream 6: customers rollup ─────────────────────────────────────────────
export interface OrgCustomersRollupBranch {
  tenantId: string
  brand: string
  displayName: string
  customerCount: number
  newCount: number
  activeBuyerCount: number
  recurringCount: number
}

export interface OrgCustomersRollup {
  orgId: string
  orgSlug: string
  orgName: string
  period: { from: string | null; to: string | null }
  branches: OrgCustomersRollupBranch[]
  totals: {
    customerCount: number
    newCount: number
    activeBuyerCount: number
    recurringCount: number
    branchCount: number
  }
}

const STUB_CUSTOMERS: OrgCustomersRollup = {
  orgId: STUB_ROLLUP.orgId,
  orgSlug: STUB_ROLLUP.orgSlug,
  orgName: STUB_ROLLUP.orgName,
  period: { from: null, to: null },
  branches: [
    { tenantId: STUB_ROLLUP.branches[0]!.tenantId, brand: STUB_ROLLUP.branches[0]!.brand, displayName: STUB_ROLLUP.branches[0]!.displayName, customerCount: 420, newCount: 18, activeBuyerCount: 89, recurringCount: 42 },
    { tenantId: STUB_ROLLUP.branches[1]!.tenantId, brand: STUB_ROLLUP.branches[1]!.brand, displayName: STUB_ROLLUP.branches[1]!.displayName, customerCount: 312, newCount: 9, activeBuyerCount: 65, recurringCount: 28 },
  ],
  totals: { customerCount: 732, newCount: 27, activeBuyerCount: 154, recurringCount: 70, branchCount: 2 },
}

export async function getOrgCustomersRollup(
  orgId: string,
  period: { from?: string; to?: string } = {},
  signal?: AbortSignal,
): Promise<OrgCustomersRollup> {
  if (PREVIEW_STUB) return mock({ ...STUB_CUSTOMERS, orgId, period: { from: period.from ?? null, to: period.to ?? null } })
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(OrgCustomersRollupQuery, { orgId, ...(period.from ? { from: period.from } : {}), ...(period.to ? { to: period.to } : {}) }, ctx).toPromise()
  if (res.error) throw res.error
  const d = res.data?.orgCustomersRollup
  if (!d) throw new Error(NO_ORG)
  return d as OrgCustomersRollup
}

// ── Stream 6: top products rollup ──────────────────────────────────────────
export interface OrgTopProductBranchBreakdown {
  tenantId: string
  brand: string
  quantity: number
  orderCount: number
}

export interface OrgTopProductRow {
  productId: string
  name: string
  totalQuantity: number
  lineCount: number
  orderCount: number
  byBranch: OrgTopProductBranchBreakdown[]
}

export interface OrgTopProductsRollup {
  orgId: string
  orgSlug: string
  orgName: string
  period: { from: string | null; to: string | null }
  limit: number
  products: OrgTopProductRow[]
}

const STUB_PRODUCTS: OrgTopProductsRollup = {
  orgId: STUB_ROLLUP.orgId,
  orgSlug: STUB_ROLLUP.orgSlug,
  orgName: STUB_ROLLUP.orgName,
  period: { from: null, to: null },
  limit: 10,
  products: [
    {
      productId: 'p-cap',
      name: 'Cappuccino',
      totalQuantity: 312,
      lineCount: 310,
      orderCount: 280,
      byBranch: [
        { tenantId: STUB_ROLLUP.branches[0]!.tenantId, brand: STUB_ROLLUP.branches[0]!.brand, quantity: 180, orderCount: 165 },
        { tenantId: STUB_ROLLUP.branches[1]!.tenantId, brand: STUB_ROLLUP.branches[1]!.brand, quantity: 132, orderCount: 115 },
      ],
    },
  ],
}

export async function getOrgTopProductsRollup(
  orgId: string,
  period: { from?: string; to?: string } = {},
  limit = 10,
  signal?: AbortSignal,
): Promise<OrgTopProductsRollup> {
  if (PREVIEW_STUB) return mock({ ...STUB_PRODUCTS, orgId, period: { from: period.from ?? null, to: period.to ?? null }, limit })
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(OrgTopProductsRollupQuery, { orgId, limit, ...(period.from ? { from: period.from } : {}), ...(period.to ? { to: period.to } : {}) }, ctx).toPromise()
  if (res.error) throw res.error
  const d = res.data?.orgTopProductsRollup
  if (!d) throw new Error(NO_ORG)
  return d as OrgTopProductsRollup
}

// ── Stream 6: KPIs rollup ──────────────────────────────────────────────────
export interface OrgKpiBranchRow {
  tenantId: string
  brand: string
  displayName: string
  orderCount: number
  avgTicketCents: number
  peakHour: number | null
  peakHourOrders: number
  peakDow: number | null
  peakDowOrders: number
}

export interface OrgKpiHeatCell {
  dow: number
  hour: number
  orderCount: number
}

export interface OrgKpisRollup {
  orgId: string
  orgSlug: string
  orgName: string
  period: { from: string | null; to: string | null }
  branches: OrgKpiBranchRow[]
  totals: {
    orderCount: number
    avgTicketCents: number
    peakHour: number | null
    peakHourOrders: number
    peakDow: number | null
    peakDowOrders: number
    branchCount: number
  }
  heatmap: OrgKpiHeatCell[]
}

const STUB_KPIS: OrgKpisRollup = {
  orgId: STUB_ROLLUP.orgId,
  orgSlug: STUB_ROLLUP.orgSlug,
  orgName: STUB_ROLLUP.orgName,
  period: { from: null, to: null },
  branches: [
    { tenantId: STUB_ROLLUP.branches[0]!.tenantId, brand: STUB_ROLLUP.branches[0]!.brand, displayName: STUB_ROLLUP.branches[0]!.displayName, orderCount: 312, avgTicketCents: 12400, peakHour: 13, peakHourOrders: 48, peakDow: 6, peakDowOrders: 72 },
    { tenantId: STUB_ROLLUP.branches[1]!.tenantId, brand: STUB_ROLLUP.branches[1]!.brand, displayName: STUB_ROLLUP.branches[1]!.displayName, orderCount: 198, avgTicketCents: 11800, peakHour: 14, peakHourOrders: 30, peakDow: 0, peakDowOrders: 41 },
  ],
  totals: { orderCount: 510, avgTicketCents: 12_200, peakHour: 13, peakHourOrders: 70, peakDow: 6, peakDowOrders: 105, branchCount: 2 },
  heatmap: [
    { dow: 6, hour: 13, orderCount: 70 },
    { dow: 0, hour: 14, orderCount: 51 },
    { dow: 5, hour: 13, orderCount: 38 },
    { dow: 1, hour: 9, orderCount: 12 },
  ],
}

export async function getOrgKpisRollup(
  orgId: string,
  period: { from?: string; to?: string } = {},
  signal?: AbortSignal,
): Promise<OrgKpisRollup> {
  if (PREVIEW_STUB) return mock({ ...STUB_KPIS, orgId, period: { from: period.from ?? null, to: period.to ?? null } })
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(OrgKpisRollupQuery, { orgId, ...(period.from ? { from: period.from } : {}), ...(period.to ? { to: period.to } : {}) }, ctx).toPromise()
  if (res.error) throw res.error
  const d = res.data?.orgKpisRollup
  if (!d) throw new Error(NO_ORG)
  return d as OrgKpisRollup
}

// ── create org (V1: backend stub) ──────────────────────────────────────────
/**
 * Crear organización. El endpoint backend (POST /v1/organizations) aún no
 * existe; documentamos la intención y devolvemos un error claro hasta que
 * esté listo. En PREVIEW_STUB simulamos creando una entrada local.
 */
export async function createOrganization(input: {
  displayName: string
  ownerUserId: string
  branches?: { liveTenantId: string }[]
}): Promise<OrgSummary> {
  if (PREVIEW_STUB) {
    const id = `00000000-0000-7000-8000-${Math.random().toString(16).slice(2, 14).padStart(12, '0')}`
    const summary: OrgSummary = {
      id, displayName: input.displayName,
      slug: input.displayName.toLowerCase().replace(/\s+/g, '-'),
      branchCount: input.branches?.length ?? 0,
    }
    rememberOrg(summary)
    return mock(summary)
  }
  // TODO(backend): POST /v1/organizations { displayName, ownerUserId, branches[] }
  throw new Error('La creación de organizaciones aún no está conectada al backend (POST /v1/organizations pendiente). Por ahora se aprovisionan vía script.')
}

/** Período predefinido → from/to ISO. */
export function periodToRange(p: 'today' | 'week' | 'month' | 'all'): { from?: string; to?: string } {
  if (p === 'all') return {}
  const now = new Date()
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString()
  let from: Date
  if (p === 'today') {
    from = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  } else if (p === 'week') {
    from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7)
  } else {
    from = new Date(now.getFullYear(), now.getMonth(), 1)
  }
  return { from: from.toISOString(), to }
}
